import { StateCreator, create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  Quote,
  QuoteChannel,
  QuotesPerChannel,
} from "@/interfaces/quoter.interface";
import { AxiosError } from "axios";
import { QuoterService } from "@/services/quoter.service";

interface QuoterState {
  quote: Quote | null;
  quotesPerChannel: QuotesPerChannel | null;
  setQuote: (quote: Quote | null) => void;
  resetQuote: () => void;
  getQuote: (
    countryCode: string,
    currencyCode: string,
    amount: number,
    payerQuote: string,
    isReceiveAmount: boolean
  ) => Promise<void>;
}

const storeApi: StateCreator<QuoterState> = (set) => ({
  quote: null,
  quotesPerChannel: null,
  setQuote(quote) {
    set(() => ({ quote }));
  },
  resetQuote() {
    set(() => ({ quote: null, quotesPerChannel: null }));
  },
  getQuote: async (
    countryCode: string,
    currencyCode: string,
    amount: number,
    payerQuote: string,
    isReceiveAmount: boolean
  ) => {
    try {
      let responseBestFx: Quote | null = null;
      let responseNoFees: Quote | null = null;

      await Promise.all([
        QuoterService.getQuote(
          countryCode,
          currencyCode,
          amount,
          payerQuote,
          isReceiveAmount,
          QuoteChannel.bestFx
        )
          .then((response) => (responseBestFx = response))
          .catch((error) => {
            console.error(error);
          }),
        QuoterService.getQuote(
          countryCode,
          currencyCode,
          amount,
          payerQuote,
          isReceiveAmount,
          QuoteChannel.lessFee
        )
          .then((response) => (responseNoFees = response))
          .catch((error) => {
            console.error(error);
          }),
      ]);

      set(() => ({
        quote: responseBestFx || null,
      }));

      set(() => ({
        quotesPerChannel: {
          [QuoteChannel.bestFx]: responseBestFx!,
          [QuoteChannel.lessFee]: responseNoFees!,
        },
      }));
    } catch (error) {
      set(() => ({
        quote: null,
        quotesPerChannel: null,
      }));
      if (error instanceof AxiosError) {
        throw new Error(error.response?.data);
      }

      throw new Error("Unable to load countries");
    }
  },
});

export const useQuoterStore = create<QuoterState>()(
  devtools(storeApi, { name: "quoter-storage" })
);
